import { Printer, PrintJob, User } from "@lemmonhq/types";
import { fetchWithNativeBridge } from "@app/utils/clientBusiness";
import { isPackaged, needsFetchBridge } from "@lemmonhq/orders";
import { getAPIClient } from "../helpers";
import { instrumentation } from "@lemmonhq/analytics";
import { nanoid } from "nanoid";
import {
  getReportingClientInfo,
  getReportingDeviceId,
  parseEposResponse,
  reportPrintJobStatus,
} from "./printJobStatusReporter";
import { printViaNative, supportsNativePrint } from "./nativePrint";

export const getPrinterHost = (printer: Printer) => {
  const protocol = isPackaged ? "http" : "https";
  return `${protocol}://${printer.ip}`;
};

const localFetch = needsFetchBridge ? fetchWithNativeBridge : fetch;

// ePOS-Print endpoint; the printer-side timeout (10 s) is in the url.
const getEposUrl = (printer: Printer) =>
  `${getPrinterHost(printer)}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`;

// Types for the new printer management API
export interface PrinterFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePrinterData {
  name: string;
  ip?: string;
  port?: string;
  serial_number?: string;
  device_id?: string;
  print_receipt?: number;
  p_category_ids?: number[];
}

export interface UpdatePrinterData {
  name?: string;
  ip?: string;
  port?: string;
  serial_number?: string;
  device_id?: string;
  print_receipt?: number;
  p_category_ids?: number[];
}
//
export const printersAPI = {
  // Existing printer job functions
  printJob: async (
    printers: Printer[],
    job: PrintJob,
    user?: User,
    printJobId?: string,
  ) => {
    const printDocId = job.doc.docId;
    console.log("printing", {
      job,
      printers,
      needsFetchBridge,
      localFetch,
      printDocId,
      printJobId,
    });
    const printer = printers.find((el) => el.id === job.printerId);
    const trackingProps = {
      userId: user?.id,
      userName: user?.profile?.name,
      userEmail: user?.profile?.email,
      restaurantId: user?.restaurant?.id,
      restaurantName: user?.restaurant?.name,
      printJobPrinterId: job.printerId,
      printDocType: job.doc.type,
      printPayloadLength: job.doc.payload.content.length,
      matchedPrinterId: printer?.id,
      matchedPrinterName: printer?.name,
      matchedPrinterIp: printer?.ip,
      matchedPrinterSerialNumber: printer?.serial_number,
      printerCount: printers.length,
      needsFetchBridge,
      isPackaged,
      printDocId,
      printJobId,
    };
    instrumentation.track("printer_job_called", trackingProps);
    if (!isPackaged) return;
    // Best-effort status tracking (print_job_statuses); never affects the print itself.
    // Only kitchen tickets are tracked — receipts aren't.
    const restaurantId = user?.restaurant?.id;
    const statusTracking =
      user?.restaurant?.feature_flags?.print_job_tracking &&
      restaurantId != null &&
      job.doc.type === "kitchenTicket"
        ? {
            jobId: printJobId || nanoid(),
            restaurantId,
            printerId: job.printerId ?? null,
            docType: job.doc.type,
            docId: printDocId ?? null,
            deviceId: getReportingDeviceId(),
            clientInfo: getReportingClientInfo(),
            clientStartedAt: new Date().toISOString(),
            startedAtMs: Date.now(),
          }
        : null;
    // Kitchen tickets go to the native app when it and the restaurant allow
    // it (nativePrint.ts); native then reports those that carry a reportId.
    const nativePrinter =
      printer && job.doc.type === "kitchenTicket" && supportsNativePrint(user)
        ? printer
        : null;
    const nativeReports = nativePrinter != null && !!job.doc.reportId;
    const reportStatus = (
      status: "sending" | "printed" | "failed",
      extra: {
        errorMessage?: string;
        printerSuccess?: boolean | null;
        printerCode?: string | null;
      } = {},
    ) => {
      if (!statusTracking || nativeReports) return;
      const { startedAtMs, ...base } = statusTracking;
      const done = status !== "sending";
      reportPrintJobStatus({
        ...base,
        status,
        ...extra,
        durationMs: done ? Date.now() - startedAtMs : null,
        completedAt: done ? new Date().toISOString() : null,
      });
    };
    if (nativePrinter) {
      reportStatus("sending");
      let result: Awaited<ReturnType<typeof printViaNative>> | null = null;
      try {
        result = await printViaNative({
          url: getEposUrl(nativePrinter),
          body: job.doc.payload.content,
          report: job.doc.reportId
            ? {
                reportId: job.doc.reportId,
                deviceId: getReportingDeviceId(),
                clientInfo: getReportingClientInfo(),
              }
            : undefined,
        });
        // ok is false for HTTP errors and for a printer that answered
        // success="false" (out of paper, cover open): not printed.
        if (!result.ok) {
          throw new Error(result.errorMessage || "[printers][ip] failed to print");
        }
        instrumentation.track("printer_job_succeeded", {
          ...trackingProps,
          nativePrint: true,
          responseOk: true,
          responseStatus: result.status,
          printerCode: result.printerCode,
        });
        reportStatus("printed", {
          printerSuccess: result.printerSuccess,
          printerCode: result.printerCode,
        });
        return;
      } catch (e) {
        console.log("print failed", e);
        const errorMessage = e instanceof Error ? e.message : String(e);
        instrumentation.track("printer_job_failed", {
          ...trackingProps,
          nativePrint: true,
          errorMessage,
        });
        reportStatus("failed", {
          errorMessage,
          printerSuccess: result?.printerSuccess ?? null,
          printerCode: result?.printerCode ?? null,
        });
        throw e;
      }
    }
    reportStatus("sending");
    try {
      console.log("printer", printer);
      if (!printer) throw new Error("printer not found");
      const url = getEposUrl(printer);
      console.log("url", url);
      const req = await localFetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "text/xml; charset=utf-8",
          SOAPAction: '""',
        },
        body: job.doc.payload.content,
        referrerPolicy: "unsafe-url",
        // must stay above the printer-side ePOS timeout (10s in the url) so we get the printer's reply
        timeoutSeconds: 30,
      });
      console.log("printed = ", req.ok);
      if (!req.ok) {
        throw new Error((await req.text()) || "[printers][ip] failed to print");
      }
      instrumentation.track("printer_job_succeeded", {
        ...trackingProps,
        responseOk: req.ok,
        responseStatus: req.status,
      });
      if (statusTracking) {
        // Read the body in the background so tracking never delays the print call.
        void req
          .text()
          .then((body) => parseEposResponse(body))
          .catch(() => ({ success: null, code: null }))
          .then((verdict) =>
            // HTTP 200 doesn't mean it printed: the printer can reject the
            // job in the body (success="false", e.g. out of paper).
            verdict.success === false
              ? reportStatus("failed", {
                  errorMessage: verdict.code ?? "Printer rejected the job",
                  printerSuccess: false,
                  printerCode: verdict.code,
                })
              : reportStatus("printed", {
                  printerSuccess: verdict.success,
                  printerCode: verdict.code,
                }),
          );
      }
    } catch (e) {
      console.log("print failed", e);
      instrumentation.track("printer_job_failed", {
        ...trackingProps,
        errorMessage: e instanceof Error ? e.message : String(e),
      });
      reportStatus("failed", {
        errorMessage: e instanceof Error ? e.message : String(e),
      });
      throw e;
    }
  },
  pingPrinter: async (printer: Printer) => {
    if (!isPackaged) return;
    let url = `${getPrinterHost(printer)}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`;
    //url = "https://web-staging.thelemmon.ch";
    try {
      const req = await localFetch(url, {
        method: "head",
        referrerPolicy: "unsafe-url",
      });
      return req.ok ? true : false;
    } catch (e) {
      // only on localhost
      if (import.meta.env.VITE_LOCALHOST) {
        const val = Math.random();
        //        console.log(
        //          "returning random value for pingPrinters on localhost",
        //          val
        //        );
        return val > 0.5 ? true : false;
      }
      return undefined;
    }
  },

  // New printer management functions following REST architecture
  getPrintersV2: async (filters?: PrinterFilters): Promise<Printer[]> => {
    const hc = getAPIClient();
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const resp = await hc.api.v2.printers.$get({
      query: Object.fromEntries(params.entries()),
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch printers");
    }

    const json = await resp.json();
    console.log("json", json);
    return json as Printer[];
  },

  getPrinter: async (id: number): Promise<Printer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.printers[":id"].$get({
      param: { id: id.toString() },
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch printer");
    }

    const json = await resp.json();
    return json as Printer;
  },

  createPrinter: async (data: CreatePrinterData): Promise<Printer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.printers.$post({
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to create printer");
    }

    const json = await resp.json();
    return json as Printer;
  },

  updatePrinter: async (
    id: number,
    data: UpdatePrinterData,
  ): Promise<Printer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.printers[":id"].$patch({
      param: { id: id.toString() },
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to update printer");
    }

    const json = await resp.json();
    return json as Printer;
  },

  deletePrinter: async (
    id: number,
  ): Promise<{ success: boolean; message: string }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.printers[":id"].$delete({
      param: { id: id.toString() },
    });

    if (!resp.ok) {
      throw new Error("Failed to delete printer");
    }

    const json = await resp.json();
    return json as { success: boolean; message: string };
  },
};
